/**
 * The quiet toast. Announced politely rather than assertively: a new catalog is
 * information, not an interruption, and `alert` would cut across whatever a
 * screen reader is currently saying.
 *
 * It never steals focus and never auto-dismisses. Auto-dismissal is a race the
 * reader always loses if they happen to be mid-sentence, and this one has an
 * action attached, so it waits to be answered.
 */

import type { ReactNode } from "react";
import styles from "./Toast.module.css";

export function Toast({
  children,
  action,
  actionLabel,
  onDismiss,
  busy = false,
}: {
  children: ReactNode;
  action?: (() => void) | undefined;
  actionLabel?: string | undefined;
  onDismiss: () => void;
  busy?: boolean | undefined;
}) {
  return (
    <div className={styles.toast} role="status" aria-live="polite">
      <p className={styles.message}>{children}</p>
      <div className={styles.actions}>
        {action && actionLabel && (
          <button type="button" className={styles.primary} onClick={action} disabled={busy}>
            {busy ? "Updating…" : actionLabel}
          </button>
        )}
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
